import { useEffect, useState } from 'react';
import { api } from './api';
import { Card, ErrorNotice, Field } from './components';

export interface WorkoutLocation { id: string; name: string; isDefault: boolean }
export interface LocationSelection { locations: WorkoutLocation[]; selectedLocationId: string }

export function LocationPicker({ profileId, onSelected, onBusy, disabled = false }: { profileId: string; onSelected: (locationId:string) => void | Promise<void>; onBusy?:(busy:boolean)=>void; disabled?: boolean }) {
  const [data, setData] = useState<LocationSelection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    api<LocationSelection>('/locations').then(value => { if (current) setData(value); }).catch(e => { if(current) setError(e.message); });
    return () => { current = false; };
  }, [profileId]);
  return <Card>
    {error && <ErrorNotice message={error}/>}
    <Field label="Where are you working out?">
      <select value={data?.selectedLocationId ?? ''} disabled={disabled || busy || !data} onChange={async event => {
        const locationId = event.target.value;
        setBusy(true); onBusy?.(true); setError('');
        try {
          await api('/locations/selected', {method:'PUT', body:JSON.stringify({locationId})});
          setData(value => value ? {...value, selectedLocationId:locationId} : value);
          await onSelected(locationId);
        } catch(e) { setError(e instanceof Error ? e.message : 'Could not change location.'); }
        finally { setBusy(false); onBusy?.(false); }
      }}>
        {!data && <option value="">Loading locations…</option>}
        {data?.locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
      </select>
    </Field>
    <p className="muted small">Each location keeps its own next workout. Switching locations does not start, skip or discard a workout. Add locations in Settings.</p>
  </Card>;
}

export function LocationManager({profileId}:{profileId:string}) {
  const [locations,setLocations] = useState<WorkoutLocation[]>([]);
  const [name,setName] = useState('');
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  useEffect(() => {
    let current=true;
    api<LocationSelection>('/locations').then(data=>{if(current)setLocations(data.locations);}).catch(e=>{if(current)setError(e.message);});
    return ()=>{current=false;};
  },[profileId]);
  return <details className="card routine-create"><summary>Workout locations</summary>
    <p>Locations and routines belong to this profile only.</p>
    <ul>{locations.map(location=><li key={location.id}>{location.name}</li>)}</ul>
    {error&&<ErrorNotice message={error}/>}
    <form onSubmit={async event=>{
      event.preventDefault(); if(busy||!name.trim())return;
      setBusy(true);setError('');
      try {
        const added=await api<WorkoutLocation>('/locations',{method:'POST',body:JSON.stringify({name:name.trim()})});
        setLocations(items=>items.some(item=>item.id===added.id)?items:[...items,added]);setName('');
      } catch(e){setError(e instanceof Error?e.message:'Could not add location.');}
      finally{setBusy(false);}
    }}>
      <Field label="New location name"><input required maxLength={80} value={name} onChange={event=>setName(event.target.value)} disabled={busy}/></Field>
      <button className="button button--secondary" disabled={busy||!name.trim()}>Add location</button>
    </form>
    <p className="muted small">New locations start empty. Select the location on Home, then add its workouts in Routine.</p>
  </details>;
}
